import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/general/Button';

import { useUserCountry } from 'hooks/useUserCountry';

import { isPrivacyRequiredCountry } from 'utils/config/privacyRegions';

const PRIVACY_ACCEPTED_KEY = 'privacy_policy_accepted';
const COOKIE_PREFERENCE_KEY = 'cookie_preference';

interface PrivacyPopupProps {
  onAccept?: (acceptAll: boolean) => void;
}

export function PrivacyPopup({ onAccept }: PrivacyPopupProps) {
  const { t } = useTranslation('login');
  const { countryCode, isLoading } = useUserCountry();
  const [hasAccepted, setHasAccepted] = useState(
    () => !!localStorage.getItem(PRIVACY_ACCEPTED_KEY)
  );

  const shouldShow =
    !isLoading &&
    !hasAccepted &&
    countryCode &&
    isPrivacyRequiredCountry(countryCode);

  const handleAccept = useCallback(
    (acceptAll: boolean) => {
      localStorage.setItem(PRIVACY_ACCEPTED_KEY, 'true');
      localStorage.setItem(
        COOKIE_PREFERENCE_KEY,
        acceptAll ? 'all' : 'essential'
      );
      setHasAccepted(true);
      onAccept?.(acceptAll);
    },
    [onAccept]
  );

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="fixed bottom-[80px] left-1/2 -translate-x-1/2 p-5 bg-white rounded-lg w-[calc(100%-2rem)] max-w-[600px] z-50">
      <p className="text-gray-700 text-sm leading-relaxed mb-4">
        {t('privacyPopup.cookieDescription')}
      </p>
      <div className="flex gap-3">
        <Button
          className="flex-1 shadow-none!"
          onClick={() => handleAccept(false)}
        >
          {t('privacyPopup.acceptEssential')}
        </Button>
        <Button
          type="primary"
          className="flex-1 shadow-none!"
          onClick={() => handleAccept(true)}
        >
          {t('privacyPopup.acceptAll')}
        </Button>
      </div>
    </div>
  );
}
